/*
 * Reconciliation surface — drift between source DHIS2 and platform state,
 * tracker buckets vs. submitted-aggregate facts, plus a "run DHIS2
 * reconciliation" trigger that enqueues a job. Platform operators see the
 * full corpus; workspace users see only their own drift summary and
 * tracker-vs-submitted comparison plus the run trigger (if their role
 * carries `workspace.datasets.manage`).
 *
 * Backend endpoints (already built):
 *   GET  /api/reconciliation              — workspace-aware drift summary
 *   GET  /api/aggregate-reconciliation    — workspace-aware tracker↔submitted comparison
 *   POST /api/reconciliation/dhis2/run    — enqueue DHIS2 reconciliation job
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";

type Summary = Awaited<ReturnType<typeof api.reconciliation>>;
type AggSummary = Awaited<ReturnType<typeof api.aggregateReconciliation>>;

type RunScope = "aggregate" | "tracker";

export default function Reconciliation({ scope = "workspace" }: { scope?: "workspace" | "platform" }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [agg, setAgg] = useState<AggSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runScope, setRunScope] = useState<RunScope>("aggregate");
  const [runMessage, setRunMessage] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  function refresh() {
    setError(null);
    Promise.all([
      api.reconciliation(),
      api.aggregateReconciliation().catch(() => null)
    ])
      .then(([s, a]) => { setSummary(s); setAgg(a); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }
  useEffect(refresh, []);

  async function runDhis2() {
    setRunning(true);
    setRunMessage(null);
    setError(null);
    try {
      const r = await api.runDhis2Reconciliation({ scope: runScope });
      setRunMessage(`Reconciliation job ${r.id} queued (status: ${r.status}). Watch progress in Sync jobs.`);
      // refresh summary shortly after — the audit log entry that drives
      // lastReconciliationAt is written immediately by the dispatcher.
      setTimeout(refresh, 800);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }

  const comparisons = agg?.comparisons ?? [];
  const significantVariances = comparisons.filter((c) => c.variance !== 0).slice(0, 100);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Reconciliation</h1>
          <p className="muted">
            Compare what the source DHIS2 instance says with what the platform tallied. Discrepancies
            here usually mean a mutation was rejected, a metadata version skew, or a data-type mismatch
            that suppressed a contribution. Use this surface to see the drift, run a reconciliation
            pass, and route findings to the dead-letter / replay flows for repair.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {runMessage && <div className="ts-alert ts-alert-info">{runMessage}</div>}

      {/* Drift summary — workspace-scoped server-side. */}
      <section className="card" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 14 }}>Drift summary</h2>
        {summary === null ? (
          <p className="muted" style={{ fontSize: 12 }}>Loading…</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 12 }}>
            <SummaryStat label="Last reconciliation" value={summary.lastReconciliationAt ? new Date(summary.lastReconciliationAt).toLocaleString() : "Never"} />
            <SummaryStat label="Drift count" value={summary.driftCount.toLocaleString()} accent={summary.driftCount > 0 ? "warn" : "ok"} />
            <SummaryStat label="Failed mutations" value={summary.failedMutations.toLocaleString()} accent={summary.failedMutations > 0 ? "warn" : "ok"} />
            <SummaryStat label="Warehouse status" value={summary.warehouseStatus} accent={summary.warehouseStatus === "ready" ? "ok" : "warn"} />
          </div>
        )}
        {summary?.recentWarnings && summary.recentWarnings.length > 0 && (
          <ul style={{ marginTop: 12, fontSize: 12, paddingLeft: 18 }}>
            {summary.recentWarnings.map((w, i) => <li key={i} className="muted">{w}</li>)}
          </ul>
        )}
      </section>

      {/* Run a reconciliation pass. */}
      <section className="card" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 14 }}>Run reconciliation</h2>
        <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Enqueues a job that re-fetches the chosen DHIS2 scope and compares against the platform's
          state. Aggregate scope hits <code>/api/dataValueSets</code>; tracker scope hits
          <code> /api/tracker/events</code>. Variances appear in the comparison table after the run
          completes.
        </p>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <label className="transfer-filter" style={{ minWidth: 160 }}>
            Scope
            <select value={runScope} onChange={(e) => setRunScope(e.target.value as RunScope)}>
              <option value="aggregate">Aggregate (data sets)</option>
              <option value="tracker">Tracker (events)</option>
            </select>
          </label>
          <button type="button" className="btn btn-primary" onClick={() => void runDhis2()} disabled={running}>
            {running ? "Queuing…" : "Run reconciliation"}
          </button>
        </div>
      </section>

      {/* Tracker vs submitted-aggregate comparison. */}
      <section className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 14 }}>Tracker vs. submitted-aggregate variances</h2>
          {agg?.latest && (
            <span className="muted" style={{ fontSize: 11 }}>
              Last run {agg.latest.completedAt ? new Date(agg.latest.completedAt).toLocaleString() : "in progress"} ·{" "}
              tracker variances: <strong>{agg.latest.trackerVarianceCount}</strong> · aggregate variances:{" "}
              <strong>{agg.latest.sourceFactVarianceCount}</strong>
            </span>
          )}
        </div>
        {significantVariances.length === 0 ? (
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            No variances recorded — every tracker-derived total matches the submitted aggregate fact, or no hybrid aggregate domains exist yet.
          </div>
        ) : (
          <table className="transfer-table" style={{ fontSize: 12, marginTop: 8 }}>
            <thead>
              <tr>
                <th>Domain</th><th>Period</th><th>Org unit</th>
                <th style={{ textAlign: "right" }}>Tracker</th>
                <th style={{ textAlign: "right" }}>Submitted</th>
                <th style={{ textAlign: "right" }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {significantVariances.map((c, i) => (
                <tr key={`${c.domainId}-${c.periodId}-${c.orgUnitId}-${i}`}>
                  <td className="mono" style={{ fontSize: 11 }}>{c.domainId}</td>
                  <td>{c.periodId}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{c.orgUnitId}</td>
                  <td style={{ textAlign: "right" }}>{c.trackerValue}</td>
                  <td style={{ textAlign: "right" }}>{c.submittedValue}</td>
                  <td
                    style={{
                      textAlign: "right",
                      color: c.variance > 0 ? "#7f1d1d" : c.variance < 0 ? "#1d4ed8" : "inherit",
                      fontWeight: 600
                    }}
                  >
                    {c.variance > 0 ? `+${c.variance}` : c.variance}
                  </td>
                </tr>
              ))}
              {comparisons.length > significantVariances.length && (
                <tr>
                  <td colSpan={6} className="muted">
                    … {comparisons.length - significantVariances.length} more rows (zero-variance suppressed)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function SummaryStat({ label, value, accent }: { label: string; value: string; accent?: "ok" | "warn" }) {
  const color = accent === "ok" ? "#065f46" : accent === "warn" ? "#92400e" : "#1e293b";
  const bg = accent === "ok" ? "#d1fae5" : accent === "warn" ? "#fef3c7" : "#f1f5f9";
  return (
    <div style={{ background: bg, color, padding: "10px 12px", borderRadius: 8 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}
